import axios from "axios"
import { useFormik } from "formik"
import { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { number, object, ObjectSchema, string } from "yup"

import { ProjectFormPageProps } from "components/ProjectForm"
import {
    IProjectBasicDetailsForm,
    setDemolitionPendingDealDocs,
    setProjectBasicDetailsForm,
    setProjectId,
} from "components/ProjectForm/ProjectFormSlice"
import { EFileTypes } from "enums/fileTypeEnums"
import EModuleIds, { ESubModuleIds } from "enums/ModuleEnum"
import {
    useGetAllProgramsListQuery,
    useGetDealDoneByUsersQuery,
    useGetReasonsQuery,
} from "pages/Common/commonService"
import { DropdownObj } from "pages/Common/commonTypes"
import {
    DocHubUrlInfo,
    SubmitFileInfo,
    useSubmitFinalUploadsMutation,
    useUploadDocumentsMutation,
} from "pages/Common/fileUploadService"
import { useAddProjectTicketMutation } from "pages/Projects/ProjectServices"
import useProjectCategoryOptions from "hooks/useProjectCategoryOptions"
import { TStore } from "store/Store"

export enum EProjectBasicDetailsFields {
    PROJECT_NAME = "project_name",
    DEAL_DONE_BY = "deal_done_by_id",
    PROJECT_CLOSURE_DATE = "project_closure_date",
    PROGRAM = "program_id",
    CATEGORY = "category_id",
    DEAL_DOCS = "deal_docs",
    DEMOLITION_REASON = "demolition_reason",
}

export type ProjectBasicDetailsFormValues = {
    [EProjectBasicDetailsFields.PROJECT_NAME]: string
    [EProjectBasicDetailsFields.DEAL_DONE_BY]: DropdownObj | null
    [EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE]: string
    [EProjectBasicDetailsFields.PROGRAM]: DropdownObj | null
    [EProjectBasicDetailsFields.CATEGORY]: DropdownObj | null
    [EProjectBasicDetailsFields.DEAL_DOCS]: string | null | undefined
    [EProjectBasicDetailsFields.DEMOLITION_REASON]: DropdownObj | null
}

const initialValues: ProjectBasicDetailsFormValues = {
    [EProjectBasicDetailsFields.PROJECT_NAME]: "",
    [EProjectBasicDetailsFields.DEAL_DONE_BY]: null,
    [EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE]: "",
    [EProjectBasicDetailsFields.PROGRAM]: null,
    [EProjectBasicDetailsFields.CATEGORY]: null,
    [EProjectBasicDetailsFields.DEAL_DOCS]: "",
    [EProjectBasicDetailsFields.DEMOLITION_REASON]: null,
}

const dropdownSchema = object({
    id: number().required(),
    title: string().required(),
}).nullable()

const optionalStringSchema = string().trim().nullable().notRequired()
const requiredStringSchema = string().trim().required("This field is required")
const requiredDropdownSchema = dropdownSchema.required("This field is required")

const basicDetailsValidationSchema: ObjectSchema<ProjectBasicDetailsFormValues> = object({
    [EProjectBasicDetailsFields.PROJECT_NAME]: requiredStringSchema.label("Project Name"),
    [EProjectBasicDetailsFields.DEAL_DONE_BY]: requiredDropdownSchema.label("Deal Done by"),
    [EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE]: requiredStringSchema.label("Expected Project Closure Date"),
    [EProjectBasicDetailsFields.PROGRAM]: dropdownSchema,
    // Mandatory per the PRD — the category is the one fact the template cannot tell us.
    [EProjectBasicDetailsFields.CATEGORY]: requiredDropdownSchema.label("Project Category"),
    [EProjectBasicDetailsFields.DEAL_DOCS]: optionalStringSchema,
    [EProjectBasicDetailsFields.DEMOLITION_REASON]: requiredDropdownSchema.label("Demolition Reason"),
})

const useDemolitionsBasicDetails = (props: ProjectFormPageProps) => {
    const dispatch = useDispatch()
    const { account_n_sites_form, project_basic_details_form, project_id, deal_details_form } = useSelector(
        (state: TStore) => state.ProjectForm
    )
    const { user_id } = useSelector((store: any) => store.verifyLogin)

    const { data: programsData, isLoading: programsLoading } = useGetAllProgramsListQuery()
    // Raise-form picker: system categories are written only by automations, so they are excluded.
    const { options: categoryOptions, isFetching: isCategoriesFetching } = useProjectCategoryOptions()
    const { data: dealDoneByUsersList } = useGetDealDoneByUsersQuery()
    const { data: demolitionReasonsData } = useGetReasonsQuery({
        module_id: EModuleIds.OPERATIONS,
        submodule_id: ESubModuleIds.DEMOLITION,
    })
    const [addProjectTicket, { isLoading: isAddingProjectTicket }] = useAddProjectTicketMutation()
    const [uploadDocuments] = useUploadDocumentsMutation()
    const [submitFinalUploads] = useSubmitFinalUploadsMutation()

    const [selectedFileType, setSelectedFileType] = useState<{ id: string; title: string } | null>(null)
    const [pendingDealDocsFiles, setPendingDealDocsFiles] = useState<File[]>([])
    const [pendingFileInfos, setPendingFileInfos] = useState<SubmitFileInfo[]>([])
    const [isUploadingImages, setIsUploadingImages] = useState(false)

    const fileTypes = [
        {
            id: EFileTypes.AGREEMENT_DOC,
            title: "Agreement",
        },
        {
            id: EFileTypes.NOC_HOST,
            title: "NOC File",
        },
        {
            id: EFileTypes.KYC_DOCUMENT,
            title: "Company registration document",
        },
        {
            id: EFileTypes.OTHER_DOC,
            title: "Others",
        },
    ]

    const formik = useFormik<ProjectBasicDetailsFormValues>({
        initialValues: project_basic_details_form
            ? {
                  ...initialValues,
                  ...project_basic_details_form,
                  [EProjectBasicDetailsFields.DEMOLITION_REASON]:
                      (project_basic_details_form as any)[EProjectBasicDetailsFields.DEMOLITION_REASON] || null,
              }
            : { ...initialValues },
        validationSchema: basicDetailsValidationSchema,
        validateOnChange: false,
        onSubmit: handleSubmit,
        enableReinitialize: true,
    })

    const programsOptions =
        programsData?.map((program: any) => ({
            id: program?.program_id,
            title: `${program?.display_id} (${program?.program_name})`,
        })) || []

    const dealDoneByUsersOptions = useMemo(() => {
        return (
            dealDoneByUsersList?.map((dealDoneByUser: any) => ({
                id: dealDoneByUser.id,
                title: dealDoneByUser.name,
            })) || []
        )
    }, [dealDoneByUsersList])

    const demolitionReasonOptions = useMemo(
        () => demolitionReasonsData?.map((r: any) => ({ id: r.id, title: r.reason })) || [],
        [demolitionReasonsData]
    )

    const hasError = (field: EProjectBasicDetailsFields) => Boolean(formik.submitCount > 0 && formik.errors[field])

    const handleAssignToMe = () => {
        const selectedUser = dealDoneByUsersOptions?.find(user => user.id === user_id)
        if (selectedUser) {
            formik.setFieldValue(EProjectBasicDetailsFields.DEAL_DONE_BY, selectedUser)
        }
    }

    async function handleSubmit(values: ProjectBasicDetailsFormValues) {
        if (props.subModuleId === ESubModuleIds.DEMOLITION && !props.isEditMode) {
            dispatch(setProjectBasicDetailsForm({ ...(values as unknown as IProjectBasicDetailsForm) }))

            if (pendingDealDocsFiles.length > 0 && selectedFileType) {
                dispatch(
                    setDemolitionPendingDealDocs({
                        file_type_id: selectedFileType.id,
                        files: pendingDealDocsFiles,
                    })
                )
            } else {
                dispatch(setDemolitionPendingDealDocs(null))
            }

            props.handleNextStep()
            return
        }

        const basicDetails: Record<string, any> = {
            [EProjectBasicDetailsFields.PROJECT_NAME]: values[EProjectBasicDetailsFields.PROJECT_NAME],
            [EProjectBasicDetailsFields.DEAL_DONE_BY]: values[EProjectBasicDetailsFields.DEAL_DONE_BY]?.id,
            [EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE]: values[EProjectBasicDetailsFields.PROJECT_CLOSURE_DATE],
            [EProjectBasicDetailsFields.PROGRAM]: values[EProjectBasicDetailsFields.PROGRAM]?.id,
            [EProjectBasicDetailsFields.CATEGORY]: values[EProjectBasicDetailsFields.CATEGORY]?.id,
            demolition_reason_id: values[EProjectBasicDetailsFields.DEMOLITION_REASON]?.id || null,
        }

        const accountsPayload = account_n_sites_form.account_roles.map(roleData => ({
            account_id: roleData.account_id,
            account_type_id: roleData.account_type_id,
            billing_address_id: roleData.billing_address_id,
            contact_id: roleData.contact_id,
            contact_type_id: roleData.contact_type_id,
        }))

        const sitePayload = {
            source_site_id: account_n_sites_form.source_site_id,
            destination_site_id: account_n_sites_form.destination_site_id,
        }

        const payload: Record<string, any> = {
            ...basicDetails,
            ...sitePayload,
            accounts: accountsPayload,
            template_id: deal_details_form.template_id?.id,
            deal_type: deal_details_form.deal_type?.id,
        }

        if (project_id) {
            payload.project_id = project_id
        }

        const res = await addProjectTicket(payload).unwrap()
        const finalProjectId = project_id || res.project_id

        if (finalProjectId) {
            if (!project_id && res.project_id) {
                dispatch(setProjectId(res.project_id))
            }

            if (project_id) {
                await submitPendingFileInfos(project_id)
            } else {
                await uploadDealDocsForNewProject(finalProjectId)
            }

            dispatch(setProjectBasicDetailsForm(values as unknown as IProjectBasicDetailsForm))

            // Demolitions have no purchase order, so there is never a payment page to add here.
            if (props.isCPODeal && props.removePaymentPage) {
                props.removePaymentPage()
            }

            props.handleNextStep()
        }
    }

    const handleDealDocsFileTypeChange = (value: { id: string; title: string } | null) => {
        setSelectedFileType(value)
    }

    const handleDealDocsUpload = (fileInfos: SubmitFileInfo[]) => {
        setPendingFileInfos(prev => [...prev, ...fileInfos])
    }

    const handleDealDocsChange = (files: FileList) => {
        setPendingDealDocsFiles(Array.from(files))
    }

    const uploadDealDocsForNewProject = async (projectId: number) => {
        if (pendingDealDocsFiles.length === 0 || !selectedFileType) return

        setIsUploadingImages(true)

        try {
            const response = await uploadDocuments({
                ticket_id: projectId,
                user_id: user_id,
                files: [{ file_type: selectedFileType.id as string, file_name: pendingDealDocsFiles.map(f => f.name) }],
            }).unwrap()

            const urls = response.urls[0]?.urls || []
            if (urls.length > 0) {
                await Promise.all(
                    urls.map(async (urlInfo: DocHubUrlInfo, index: number) => {
                        await axios.put(urlInfo.presigned_url, pendingDealDocsFiles[index], {
                            headers: { "Content-Type": pendingDealDocsFiles[index]?.type },
                        })
                    })
                )

                const fileInfos: SubmitFileInfo[] = urls.map((urlInfo: DocHubUrlInfo) => ({
                    file_url: urlInfo.file_url,
                    doc_id: urlInfo.doc_id,
                    report_id: urlInfo.report_id,
                }))

                await submitFinalUploads({
                    ticket_id: projectId,
                    file_infos: fileInfos,
                }).unwrap()
            }
        } catch (error) {
            console.error("Error uploading deal docs:", error)
        } finally {
            setIsUploadingImages(false)
        }
    }

    const submitPendingFileInfos = async (projectId: number) => {
        if (pendingFileInfos.length === 0) return

        try {
            await submitFinalUploads({
                ticket_id: projectId,
                file_infos: pendingFileInfos,
            }).unwrap()
        } catch (error) {
            console.error("Error submitting final uploads:", error)
        }
    }

    return {
        states: {
            dealDoneByUsersOptions,
            programsOptions,
            programsLoading,
            categoryOptions,
            isCategoriesFetching,
            isAddingProjectTicket,
            formik,
            fileTypes,
            selectedFileType,
            project_id,
            demolitionReasonOptions,
        },
        functions: {
            hasError,
            handleAssignToMe,
            handleDealDocsFileTypeChange,
            handleDealDocsUpload,
            handleDealDocsChange,
        },
    }
}

export default useDemolitionsBasicDetails
